import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useAuth } from '../../contexts/AuthContext';
import { shForm, shJson } from './api';

/**
 * Modal "Minhas listas" — CRUD de OptionList por setor + import planilha.
 */
export default function SafetyHuddleOptionListsDialog({ open, onClose, sectorId }) {
  const { unidadeSelecionada } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [lists, setLists] = useState([]);
  const [canManage, setCanManage] = useState(false);

  const [editList, setEditList] = useState(null);
  const [newItem, setNewItem] = useState('');

  const [importTarget, setImportTarget] = useState(null); // list | null for new
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importMode, setImportMode] = useState('replace');
  const [importName, setImportName] = useState('Colaboradores');
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    if (!open || !sectorId || !unidadeSelecionada?.codigo) return;
    setLoading(true);
    setError('');
    try {
      const data = await shJson(
        `/api/safety-huddle/sectors/${sectorId}/option-lists`,
        unidadeSelecionada
      );
      setLists(data.items || []);
      setCanManage(!!data.canManage);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [open, sectorId, unidadeSelecionada]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!open) {
      setEditList(null);
      setNewItem('');
      setError('');
    }
  }, [open]);

  const openCreate = () => {
    setEditList({ id: null, name: '', items: [] });
    setNewItem('');
  };

  const openEdit = (list) => {
    setEditList({
      id: list.id,
      name: list.name || '',
      items: [...(list.items || [])],
      tipoOpcoes: list.tipoOpcoes || 'outro',
    });
    setNewItem('');
  };

  const addItem = () => {
    const v = newItem.trim();
    if (!v || !editList) return;
    if (editList.items.some((x) => x.toLowerCase() === v.toLowerCase())) {
      setNewItem('');
      return;
    }
    setEditList((prev) => ({ ...prev, items: [...prev.items, v] }));
    setNewItem('');
  };

  const removeItem = (idx) => {
    setEditList((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== idx),
    }));
  };

  const saveList = async () => {
    if (!editList?.name?.trim()) return;
    setSaving(true);
    setError('');
    try {
      if (editList.id) {
        await shJson(`/api/safety-huddle/option-lists/${editList.id}`, unidadeSelecionada, {
          method: 'PUT',
          body: JSON.stringify({
            name: editList.name.trim(),
            items: editList.items,
            tipoOpcoes: editList.tipoOpcoes || 'outro',
          }),
        });
      } else {
        await shJson(`/api/safety-huddle/sectors/${sectorId}/option-lists`, unidadeSelecionada, {
          method: 'POST',
          body: JSON.stringify({
            name: editList.name.trim(),
            items: editList.items,
            tipoOpcoes: editList.tipoOpcoes || 'outro',
          }),
        });
      }
      setEditList(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteList = async (list) => {
    if (!window.confirm(`Excluir a lista «${list.name}»?`)) return;
    setError('');
    try {
      await shJson(`/api/safety-huddle/option-lists/${list.id}`, unidadeSelecionada, {
        method: 'DELETE',
      });
      if (editList?.id === list.id) setEditList(null);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const openImport = (list = null) => {
    setImportTarget(list);
    setImportFile(null);
    setImportMode(list ? 'replace' : 'create');
    setImportName(list?.name || 'Colaboradores');
    setImportOpen(true);
  };

  const runImport = async () => {
    if (!importFile) {
      setError('Selecione um arquivo.');
      return;
    }
    setImporting(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      fd.append('tipoOpcoes', 'pessoas');
      if (importTarget?.id) {
        fd.append('listId', String(importTarget.id));
        fd.append('mode', importMode === 'merge' ? 'merge' : 'replace');
      } else {
        fd.append('mode', 'create');
        fd.append('name', importName.trim() || 'Colaboradores');
      }
      await shForm(
        `/api/safety-huddle/sectors/${sectorId}/option-lists/import`,
        unidadeSelecionada,
        fd
      );
      setImportOpen(false);
      setImportTarget(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Minhas listas de opções</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Listas reutilizáveis neste setor (ex.: setores da reunião, colaboradores). Independentes das
          perguntas — você vincula depois ao criar uma Seleção.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : !editList ? (
          <>
            {canManage && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Button startIcon={<UploadFileIcon />} variant="outlined" onClick={() => openImport(null)}>
                  Importar planilha
                </Button>
                <Button startIcon={<AddIcon />} variant="outlined" onClick={openCreate}>
                  Nova lista
                </Button>
              </Box>
            )}
            <List dense>
              {lists.map((list) => (
                <ListItem
                  key={list.id}
                  sx={{
                    border: '1px solid rgba(25,118,210,0.12)',
                    borderRadius: 2,
                    mb: 1,
                    alignItems: 'flex-start',
                  }}
                  secondaryAction={
                    canManage ? (
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => openImport(list)}
                          aria-label="Importar planilha"
                          title="Importar/atualizar planilha"
                        >
                          <UploadFileIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => openEdit(list)} aria-label="Editar">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => deleteList(list)}
                          aria-label="Excluir"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : null
                  }
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography fontWeight={700}>{list.name}</Typography>
                        <Chip size="small" label={`${list.items.length} itens`} />
                        {list.tipoOpcoes && list.tipoOpcoes !== 'outro' && (
                          <Chip size="small" variant="outlined" label={list.tipoOpcoes} />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        {list.lastImportAt
                          ? `Importado de ${list.lastImportFile || 'planilha'} em ${new Date(
                              list.lastImportAt
                            ).toLocaleString('pt-BR')} — ${list.lastImportCount ?? list.items.length} nome(s). `
                          : ''}
                        {list.items.length
                          ? list.items.slice(0, 8).join(', ') +
                            (list.items.length > 8 ? `… (+${list.items.length - 8})` : '')
                          : 'Sem itens'}
                      </>
                    }
                  />
                </ListItem>
              ))}
              {!lists.length && (
                <Alert severity="info">
                  Nenhuma lista neste setor. Crie uma ou importe a planilha de colaboradores.
                </Alert>
              )}
            </List>
          </>
        ) : (
          <Box>
            <Button size="small" onClick={() => setEditList(null)} sx={{ mb: 1 }}>
              ← Voltar às listas
            </Button>
            <TextField
              autoFocus
              margin="dense"
              label="Nome da lista"
              fullWidth
              value={editList.name}
              onChange={(e) => setEditList((p) => ({ ...p, name: e.target.value }))}
              placeholder="Ex.: Setores da Reunião"
            />
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Itens
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
              <TextField
                size="small"
                fullWidth
                label="Novo item"
                value={newItem}
                onChange={(e) => setNewItem(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addItem();
                  }
                }}
              />
              <Button variant="outlined" startIcon={<AddIcon />} onClick={addItem} disabled={!newItem.trim()}>
                Add
              </Button>
            </Box>
            <List dense sx={{ maxHeight: 280, overflow: 'auto' }}>
              {editList.items.map((item, idx) => (
                <ListItem
                  key={`${item}-${idx}`}
                  secondaryAction={
                    <IconButton edge="end" size="small" onClick={() => removeItem(idx)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemText primary={item} />
                </ListItem>
              ))}
              {!editList.items.length && (
                <Typography variant="body2" color="text.secondary">
                  Nenhum item — adicione acima.
                </Typography>
              )}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        {editList && canManage && (
          <Button
            variant="contained"
            disabled={saving || !editList.name.trim()}
            onClick={saveList}
          >
            {saving ? 'Salvando…' : 'Salvar lista'}
          </Button>
        )}
      </DialogActions>
    </Dialog>

      <Dialog open={importOpen} onClose={() => setImportOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {importTarget ? `Atualizar «${importTarget.name}»` : 'Importar planilha'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Preferência: coluna <strong>Nome</strong>. Caso contrário, usa a primeira coluna.
          </Typography>
          {!importTarget && (
            <TextField
              margin="dense"
              label="Nome da nova lista"
              fullWidth
              value={importName}
              onChange={(e) => setImportName(e.target.value)}
            />
          )}
          {importTarget && (
            <FormControl fullWidth margin="dense" size="small">
              <InputLabel>Modo</InputLabel>
              <Select
                label="Modo"
                value={importMode}
                onChange={(e) => setImportMode(e.target.value)}
              >
                <MenuItem value="replace">Substituir lista atual</MenuItem>
                <MenuItem value="merge">Adicionar só nomes novos</MenuItem>
              </Select>
            </FormControl>
          )}
          <Button variant="outlined" component="label" sx={{ mt: 1.5 }}>
            Escolher arquivo
            <input
              hidden
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
            />
          </Button>
          {importFile && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              {importFile.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={importing || !importFile}
            onClick={runImport}
          >
            {importing ? 'Importando…' : 'Importar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
